import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { NAV_LINKS } from "@/lib/constants";
import { useCart } from "@/context/CartContext";

const MobileMenu = ({ open, setOpen, textColor, currentPath }) => (
  <Sheet open={open} onOpenChange={setOpen}>
    <SheetTrigger asChild className="lg:hidden">
      <button
        data-testid="mobile-menu-toggle"
        className={`p-2 ${textColor}`}
        aria-label="Toggle navigation menu"
      >
        {open ? <X size={24} /> : <Menu size={24} />}
      </button>
    </SheetTrigger>
    <SheetContent side="right" className="w-80 bg-background p-0">
      <div className="flex flex-col h-full pt-12">
        <div className="px-6 pb-6 border-b">
          <span className="font-heading text-2xl font-semibold text-foreground">
            Serenity Village
          </span>
        </div>
        <nav className="flex flex-col py-4">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`mobile-nav-${link.path.replace("/", "") || "home"}`}
              onClick={() => setOpen(false)}
              className={`px-6 py-3 font-body text-base transition-colors duration-200
                ${currentPath === link.path
                  ? "text-primary bg-primary/5 font-medium"
                  : "text-foreground/70 hover:text-primary hover:bg-muted/50"
                }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>
        <div className="mt-auto p-6 border-t">
          <Link to="/bet-cabin" onClick={() => setOpen(false)}>
            <Button
              data-testid="mobile-book-now"
              className="w-full bg-accent hover:bg-accent/90 text-white font-body rounded-full"
            >
              Book Now
            </Button>
          </Link>
        </div>
      </div>
    </SheetContent>
  </Sheet>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { count } = useCart();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const onDarkHero = !scrolled && isHome;
  const navBg = onDarkHero ? "bg-transparent" : "glass shadow-sm";
  const textColor = onDarkHero ? "text-white" : "text-foreground";

  const desktopLinkClass = (path) => {
    const isActive = location.pathname === path;
    if (isActive) {
      return onDarkHero ? "text-white bg-white/10" : "text-primary bg-primary/5";
    }
    return onDarkHero ? "text-white/80 hover:text-white" : "text-foreground/70 hover:text-primary";
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${navBg}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            data-testid="navbar-logo"
            className={`font-heading text-xl md:text-2xl font-semibold tracking-tight ${textColor}`}
          >
            Serenity Village
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
                className={`px-3 py-2 text-sm font-body font-medium transition-colors duration-200 rounded-md ${desktopLinkClass(link.path)}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/cart" data-testid="navbar-cart" className={`relative p-2 ${textColor}`} aria-label="Shopping cart">
              <ShoppingCart size={20} />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-0.5 -right-0.5 bg-accent text-white text-[10px] font-body font-semibold rounded-full h-4 min-w-4 px-1 flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </Link>
            <Link to="/bet-cabin" className="hidden md:block">
              <Button
                data-testid="navbar-book-now"
                className="bg-accent hover:bg-accent/90 text-white font-body text-sm px-6 rounded-full"
              >
                Book Now
              </Button>
            </Link>

            <MobileMenu open={open} setOpen={setOpen} textColor={textColor} currentPath={location.pathname} />
          </div>
        </div>
      </div>
    </header>
  );
};
